import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
// import { useAuthStore } from '../stores/authStore';
import { jwtDecode } from 'jwt-decode';
import { userManager } from '../hooks/oidcConfig';
import { Loader2 } from 'lucide-react';
import { JwtPayload, TokenObject, ErrorDetails, ErrorLogParams, OidcUser } from '@/types/auth';

// 常量定义
const REDIRECT_DELAY = 1000;
const TIME_ERROR_DELAY = 5000;
const DEFAULT_REDIRECT_PATH = '/';  // 修改默认跳转路径为首页

/**
 * 处理OIDC回调的工具函数
 */
const handleOidcCallback = async (user: OidcUser) => {
  // 存储access token
  localStorage.setItem('access_token', user.access_token);
  
  // 解析JWT token
  const jwtToken = jwtDecode<JwtPayload>(user.access_token);
  
  // 存储token相关信息
  const tokenObj: TokenObject = {
    expires_at: jwtToken.exp,
    refresh_token: user.refresh_token,
    loginIn: jwtToken.lid,
  };
  localStorage.setItem('tokenObj', JSON.stringify(tokenObj));
  
  // 处理OIDC会话存储
  const {
    VITE_OIDC_WEB_CLIENT_ID,
    VITE_OIDC_AUTHORITY_URL,
  } = import.meta.env;
  
  const name = `oidc.user:${VITE_OIDC_AUTHORITY_URL}:${VITE_OIDC_WEB_CLIENT_ID}`;
  const sessionData = sessionStorage.getItem(name);
  
  if (user.access_token && sessionData) {
    localStorage.setItem(name, sessionData);
  }
  
  return {
    sub: user.profile?.sub,
    hasToken: !!user.access_token,
    expiresAt: new Date(jwtToken.exp * 1000).toLocaleString()
  };
};

const logout = () => {
  const identityServerBase = import.meta.env.VITE_OIDC_SERVER_URL
  const client_id = import.meta.env.VITE_OIDC_CLIENT_ID
  const name = `oidc.user:${identityServerBase}:${client_id}`
  const token = localStorage.getItem('access_token')
  if (token) {
    Object.keys(localStorage).forEach(key => {
      // 如果键不是需要保留的键，则删除它
      if (key !== name) {
          localStorage.removeItem(key);
      }
    });
    userManager.signoutRedirect()
  }
}

/**
 * 错误日志记录函数
 */
const logError = async (error: ErrorDetails) => {
  const params: ErrorLogParams = {
    name: "认证回调页面错误",
    category: "callback",
    eventType: "Failure",
    message: JSON.stringify(error),
    url: `${window.location.origin}/callback`,
    data: "",
  };
  
  // TODO: 实现错误日志API调用
  console.error('错误日志:', params);
};

/**
 * 清除用户会话数据
 */
const clearSession = () => {
  localStorage.clear();
  sessionStorage.clear();
};

/**
 * OIDC回调处理组件
 * 处理SSO回调，获取token并保存用户状态
 */
const Callback = () => {
  const navigate = useNavigate();
  // const loginWithOidc = useAuthStore(state => state.loginWithOidc);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState('处理中...');

  useEffect(() => {
    const processCallback = async () => {
      try {
        setStatus('开始处理SSO回调...');
        console.log('开始处理SSO回调...');
        // 处理OIDC回调
        const user = await userManager.signinRedirectCallback();
        console.log('处理OIDC回调...',user);
        const userInfo = await handleOidcCallback(user);
        console.log('Callback处理成功，获取到用户数据:', userInfo);
        // 更新认证状态
        // loginWithOidc(user);
        setStatus('认证成功，正在跳转到目标页面...');
        
        // 处理重定向
        setTimeout(() => {
          if (typeof user.state === 'string') {
            window.location.href = user.state;
          } else {
            navigate(DEFAULT_REDIRECT_PATH, { replace: true });
          }
        }, REDIRECT_DELAY);
        
      } catch (error) {
        const err = error as Error;
        
        // 处理登录需求错误
        if (window.location.href.includes('error=login_require')) {
          clearSession();
          navigate('/login', { replace: true });
          return;
        }
        
        // 构建错误详情
        const errorDetails: ErrorDetails = {
          message: err.message,
          stack: err.stack,
          name: err.name
        };
        
        // 处理时间同步错误
        const isTimeError = err.message.includes('exp is in the past');
        const errorMessage = isTimeError
          ? '您的电脑时间与服务器不一致，请重置系统时间，获取更多操作攻略请联系IT支持。'
          : `登录处理失败: ${err.message}`;
          
        setError(errorMessage);
        
        // 记录错误并处理重定向
        try {
          await logError(errorDetails);
          setTimeout(() => {
            clearSession();
            if (!isTimeError) {
              alert('登录失败，请重试');
            }
            logout()
          }, isTimeError ? TIME_ERROR_DELAY : 0);
        } catch (loggingError) {
          console.error('记录错误信息时出错:', loggingError);
          navigate('/login', { replace: true });
        }
      }
    };

    // 只在没有access_token时处理回调
    if (!localStorage.getItem('access_token')) {
      processCallback();
    }
    }, [navigate]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center space-y-4">
          <div className="text-red-600">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center space-y-4">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto" />
        <p className="text-slate-600">{status}</p>
      </div>
    </div>
  );
};

export default Callback; 